import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Navigation2, Users, AlertTriangle, MessageSquareText } from "lucide-react";

const carSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>';

// Car badge with a rotating heading arrow that points the way the chauffeur is driving.
const carIcon = (deg = null) => {
  const arrow = deg == null ? "" : `<div style="position:absolute;top:-9px;left:50%;width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-bottom:10px solid #D4AF37;transform-origin:50% 29px;transform:translateX(-50%) rotate(${deg}deg);filter:drop-shadow(0 0 1px #000)"></div>`;
  return L.divIcon({ className: "", html: `<div style="position:relative;width:40px;height:40px"><div style="width:40px;height:40px;background:#D4AF37;border:2px solid #000;border-radius:10px;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 14px rgba(0,0,0,0.5),0 0 0 5px rgba(212,175,55,0.28)">${carSvg}</div>${arrow}</div>`, iconSize: [40, 40], iconAnchor: [20, 20] });
};
const dotIcon = (fill, ring) => L.divIcon({ className: "", html: `<div style="width:18px;height:18px;background:${fill};border:3px solid #050505;border-radius:50%;box-shadow:0 0 0 5px ${ring}"></div>`, iconSize: [18, 18], iconAnchor: [9, 9] });

const OSRM = (a, b) => fetch(`https://router.project-osrm.org/route/v1/driving/${a.lng},${a.lat};${b.lng},${b.lat}?overview=full&geometries=geojson`).then((r) => r.json());

const bearing = (a, b) => {
  const toRad = (d) => (d * Math.PI) / 180, toDeg = (r) => (r * 180) / Math.PI;
  const dLon = toRad(b.lng - a.lng);
  const y = Math.sin(dLon) * Math.cos(toRad(b.lat));
  const x = Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) - Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

const TILES = {
  night: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
  day: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
};

// Custom Uber-style ride map: labeled pins, route, heading-aware car, live ETA + progress. Tiles follow the theme.
export function RideMap({ pickup, dropoff, car, phase = "route", info = {}, onEta, className = "h-[60vh]", theme = "night" }) {
  const el = useRef(null);
  const map = useRef(null);
  const tile = useRef(null);
  const layers = useRef({});
  const prevCar = useRef(null);
  const heading = useRef(null);
  const baseEta = useRef(null);
  const fitted = useRef(false);
  const [eta, setEta] = useState(null);
  const [progress, setProgress] = useState(0);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!el.current || map.current || !pickup) return;
    const m = L.map(el.current, { zoomControl: false, attributionControl: false }).setView([pickup.lat, pickup.lng], 13);
    tile.current = L.tileLayer(TILES[theme] || TILES.night, { maxZoom: 20 }).addTo(m);
    L.control.zoom({ position: "topright" }).addTo(m);
    map.current = m;
    setTimeout(() => map.current && map.current.invalidateSize(), 150);
    return () => { if (map.current) { map.current.remove(); map.current = null; tile.current = null; layers.current = {}; fitted.current = false; } };
  }, [pickup?.lat, pickup?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!map.current || !tile.current) return;
    tile.current.setUrl(TILES[theme] || TILES.night);
  }, [theme]);

  // Re-fetch the route/ETA every 10s while the chauffeur is en route (keeps the countdown accurate).
  useEffect(() => {
    if (phase !== "arriving") return;
    const id = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(id);
  }, [phase]);

  useEffect(() => {
    const m = map.current;
    if (!m || !pickup) return;
    const L_ = layers.current;
    if (phase !== "arriving") { baseEta.current = null; setProgress(0); }

    if (!L_.pickup) {
      L_.pickup = L.marker([pickup.lat, pickup.lng], { icon: dotIcon("#D4AF37", "rgba(212,175,55,0.22)"), zIndexOffset: 500 })
        .addTo(m).bindTooltip(`<span class="rm-k">Pickup</span><br/>${pickup.name || ""}`, { permanent: true, direction: "right", className: "rm-label rm-gold", offset: [10, 0] });
    }
    if (dropoff && dropoff.lat != null && !L_.dropoff) {
      L_.dropoff = L.marker([dropoff.lat, dropoff.lng], { icon: dotIcon("#ffffff", "rgba(255,255,255,0.15)"), zIndexOffset: 500 })
        .addTo(m).bindTooltip(`<span class="rm-k">Drop-off</span><br/>${dropoff.name || ""}`, { permanent: true, direction: "left", className: "rm-label", offset: [-10, 0] });
    }

    // Live car with a heading arrow pointing the direction of travel.
    if (car && car.lat != null) {
      const prev = prevCar.current;
      if (prev && (prev.lat !== car.lat || prev.lng !== car.lng)) heading.current = bearing(prev, car);
      prevCar.current = { lat: car.lat, lng: car.lng };
      if (!L_.car) L_.car = L.marker([car.lat, car.lng], { icon: carIcon(heading.current), zIndexOffset: 1000 }).addTo(m);
      else { L_.car.setLatLng([car.lat, car.lng]); L_.car.setIcon(carIcon(heading.current)); }
    } else if (L_.car) { L_.car.remove(); L_.car = null; prevCar.current = null; heading.current = null; }

    const from = phase === "arriving" && car && car.lat != null ? car : pickup;
    const to = phase === "arriving" && car && car.lat != null ? pickup : (dropoff && dropoff.lat != null ? dropoff : null);
    let cancelled = false;
    const points = [[pickup.lat, pickup.lng]];
    if (dropoff && dropoff.lat != null) points.push([dropoff.lat, dropoff.lng]);
    if (car && car.lat != null) points.push([car.lat, car.lng]);
    const fit = () => { if (!fitted.current && map.current) { m.fitBounds(points, { padding: [60, 60], maxZoom: 15, animate: false }); fitted.current = true; } };
    if (to) {
      OSRM(from, to).then((j) => {
        if (cancelled || !map.current) return;
        const route = j.routes && j.routes[0];
        if (!route) { fit(); return; }
        if (phase === "arriving") {
          const min = Math.max(1, Math.round(route.duration / 60));
          setEta(min); onEta && onEta(min);
          if (baseEta.current == null || min > baseEta.current) baseEta.current = min;
          setProgress(baseEta.current ? Math.min(1, Math.max(0, 1 - min / baseEta.current)) : 0);
        }
        const latlngs = route.geometry.coordinates.map(([x, y]) => [y, x]);
        if (L_.route) L_.route.remove();
        L_.route = L.polyline(latlngs, { color: "#D4AF37", weight: 5, opacity: 0.9 }).addTo(m);
        if (!fitted.current) { m.fitBounds(L_.route.getBounds().extend(points), { padding: [60, 60], maxZoom: 16, animate: false }); fitted.current = true; }
      }).catch(fit);
    } else { fit(); }
    return () => { cancelled = true; };
  }, [pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng, car?.lat, car?.lng, phase, onEta, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const initials = (info.driverName || "").charAt(0).toUpperCase();
  const arriving = phase === "arriving";

  return (
    <div className={`relative w-full ${className}`} data-testid="ridemap">
      <div ref={el} className="absolute inset-0" data-testid="ridemap-canvas" />

      {(info.rideName || info.driverName || pickup) && (
        <div className="absolute left-3 right-3 bottom-3 z-[400] pointer-events-none">
          <div className="mx-auto max-w-md bg-[#050505]/92 backdrop-blur-md border border-gold/40 shadow-2xl pointer-events-auto" data-testid="ridemap-card">
            {arriving && (
              <div className="h-1 w-full bg-white/10 overflow-hidden" data-testid="ridemap-progress">
                <div className="h-full bg-gold transition-all duration-700 ease-out" style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
            )}
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="font-serif font-semibold text-lg text-white leading-none">{info.rideName || "Your ride"}</p>
                {arriving && eta != null ? (
                  <span className="flex items-center gap-1.5 label text-black bg-gold px-2.5 py-1 text-[11px] font-bold" data-testid="ridemap-eta"><Navigation2 size={12} /> ~{eta} min</span>
                ) : info.statusText ? (
                  <span className="label text-gold border border-gold/40 px-2.5 py-1 text-[11px] font-bold">{info.statusText}</span>
                ) : null}
              </div>

              {(info.driverName || info.photo) && (
                <div className="flex items-center gap-3 pb-3 mb-3 border-b border-white/10">
                  {info.photo ? (
                    <img src={info.photo} alt={info.driverName} className="w-11 h-11 rounded-full object-cover border-2 border-gold shrink-0" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-gold/15 border-2 border-gold flex items-center justify-center font-serif text-lg font-bold text-gold shrink-0">{initials || "N"}</div>
                  )}
                  <div className="min-w-0">
                    <p className="font-body font-bold text-white text-[15px] truncate leading-tight">{info.driverName || "Chauffeur assigning…"}</p>
                    {info.plate && <p className="font-body font-semibold text-white/60 text-xs truncate">{info.plate}</p>}
                  </div>
                </div>
              )}

              {info.passengers != null && (
                <div className="mb-3">
                  <span className="label text-[10px] font-bold text-white/85 border border-white/20 px-2.5 py-1 inline-flex items-center gap-1.5" data-testid="ridemap-pax">
                    <Users size={12} /> {info.passengers} passenger{Number(info.passengers) === 1 ? "" : "s"}
                  </span>
                </div>
              )}

              {info.notes && (
                <div className="mb-3 flex items-start gap-2 border border-amber-400/40 bg-amber-400/10 px-3 py-2" data-testid="ridemap-notes">
                  <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="label text-amber-400/80 text-[9px] leading-none mb-0.5">Special instructions</p>
                    <p className="font-body font-bold text-amber-100 text-xs leading-snug">{info.notes}</p>
                  </div>
                </div>
              )}

              {info.riderNote && (
                <div className="mb-3 flex items-start gap-2 border border-sky-400/40 bg-sky-400/10 px-3 py-2" data-testid="ridemap-rider-note">
                  <MessageSquareText size={14} className="text-sky-400 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="label text-sky-400/80 text-[9px] leading-none mb-0.5">Note from rider</p>
                    <p className="font-body font-bold text-sky-100 text-xs leading-snug">{info.riderNote}</p>
                  </div>
                </div>
              )}

              <div className="space-y-2.5">
                <div className="flex items-start gap-2.5">
                  <span className="w-3 h-3 rounded-full bg-gold border-2 border-black mt-0.5 shrink-0" />
                  <div className="min-w-0"><p className="label text-gold/70 text-[9px] leading-none mb-0.5">Pickup</p><p className="font-body font-bold text-white text-sm leading-tight truncate">{pickup?.name}</p></div>
                </div>
                {dropoff?.name && (
                  <div className="flex items-start gap-2.5">
                    <span className="w-3 h-3 rounded-full bg-white border-2 border-black mt-0.5 shrink-0" />
                    <div className="min-w-0"><p className="label text-white/40 text-[9px] leading-none mb-0.5">Drop-off</p><p className="font-body font-bold text-white text-sm leading-tight truncate">{dropoff.name}</p></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
